import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, HelpCircle, MapPin, Zap, DollarSign, CheckCircle2, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useAuth } from "@/components/AuthProvider";
import { NotasFiscaisBlockedModal } from "@/components/NotasFiscaisBlockedModal";
import { getPlanInfo } from "@/integrations/supabase/plan-utils";

export default function AjudaNotasFiscais() {
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const [abaCfg, setAbaCfg] = useState("inicio");
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [hasNFAccess, setHasNFAccess] = useState<boolean | null>(null);
  const accessCheckDone = useRef(false);

  useEffect(() => {
    if (accessCheckDone.current) return;
    if (!empresa?.id) return;

    accessCheckDone.current = true;

    getPlanInfo(empresa.id).then(planInfo => {
      setHasNFAccess(planInfo.canUseNF);
      if (!planInfo.canUseNF) {
        setShowBlockedModal(true);
      }
    }).catch(() => {
      setHasNFAccess(true);
    });
  }, []);

  return hasNFAccess === false ? (
    <NotasFiscaisBlockedModal
      open={showBlockedModal}
      onOpenChange={setShowBlockedModal}
    />
  ) : (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-blue-200 sticky top-0 z-10 shadow-sm">
        <div className="p-4 md:p-6">
          <div className="flex items-center gap-4">
            <SidebarTrigger className="text-blue-700 hover:bg-blue-100" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/configuracao-focusnf")}
              className="text-blue-700 hover:bg-blue-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar para Configuração
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-blue-900">Ajuda - Notas Fiscais</h1>
              <p className="text-blue-600 text-sm mt-0.5">Guia completo para configurar e emitir suas notas fiscais</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6 space-y-6 max-w-5xl">
        <Tabs value={abaCfg} onValueChange={setAbaCfg} className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-4">
            <TabsTrigger value="inicio">🚀 Início</TabsTrigger>
            <TabsTrigger value="tipos">📋 Tipos</TabsTrigger>
            <TabsTrigger value="config">⚙️ Config</TabsTrigger>
            <TabsTrigger value="ufs">📍 UFs</TabsTrigger>
          </TabsList>

          {/* Aba: Início */}
          <TabsContent value="inicio" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  Começar Rápido
                </CardTitle>
                <CardDescription>
                  3 passos para emitir sua primeira nota fiscal
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Passo 1 */}
                  <Card className="border-2 border-blue-200 bg-blue-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-lg">
                        1
                      </div>
                      <h3 className="font-bold text-blue-900">Preencha os Dados</h3>
                      <p className="text-sm text-blue-800">
                        Vá em <strong>Configuração Focus NF</strong> e preencha os dados da sua empresa: CNPJ, razão social, endereço, certificado digital e inscrição municipal.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Passo 2 */}
                  <Card className="border-2 border-indigo-200 bg-indigo-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-600 text-white font-bold text-lg">
                        2
                      </div>
                      <h3 className="font-bold text-indigo-900">Cadastre o Emitente</h3>
                      <p className="text-sm text-indigo-800">
                        Clique no botão <strong>"Cadastrar emitente na Focus"</strong> para registrar sua empresa na plataforma Focus NF.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Passo 3 */}
                  <Card className="border-2 border-purple-200 bg-purple-50">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-600 text-white font-bold text-lg">
                        3
                      </div>
                      <h3 className="font-bold text-purple-900">Emita Notas</h3>
                      <p className="text-sm text-purple-800">
                        Acesse <strong>Pedidos</strong>, <strong>NF Avulsa</strong> ou <strong>PDV</strong> e escolha o tipo de nota (NFe, NFCe, NFS-e).
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            <Card className="border-yellow-200 bg-yellow-50">
              <CardHeader>
                <CardTitle className="text-yellow-900 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  ⚠️ Importante
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-yellow-800">
                <p><strong>Certificado Digital:</strong> Obtenha um certificado A1 (arquivo .pfx ou .p12) de uma Autoridade Certificadora autorizada pela Receita Federal.</p>
                <p><strong>Inscrição Municipal:</strong> Necessária para NFS-e. Verifique se sua prefeitura local utiliza o sistema de NFS-e.</p>
                <p><strong>Homologação:</strong> Sempre teste em homologação antes de ir para produção.</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba: Tipos de Notas */}
          <TabsContent value="tipos" className="space-y-4">
            {/* NFe */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
                <CardTitle className="text-green-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  NFe - Nota Fiscal Eletrônica (Modelo 55)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📌 Quando usar?</h4>
                  <p className="text-gray-700">
                    Use para vendas de <strong>produtos</strong> (varejo, atacado) entre empresas ou para consumidor identificado. É a nota mais comum.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">✅ Dados obrigatórios</h4>
                  <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                    <li>NCM (8 dígitos) - código do produto</li>
                    <li>CFOP - classificação fiscal</li>
                    <li>Descrição do produto</li>
                    <li>Quantidade e valor unitário</li>
                    <li>Cliente identificado (CPF ou CNPJ)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">⚡ Como preencher</h4>
                  <p className="text-gray-700 text-sm">
                    Vá em <strong>Pedidos → Emitir Nota Fiscal</strong>, selecione <strong>"NFe"</strong>, adicione os itens com NCM correto, preencha dados do cliente e emita.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* NFCe */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-200">
                <CardTitle className="text-blue-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  NFCe - Nota Fiscal ao Consumidor Eletrônica (Modelo 65)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📌 Quando usar?</h4>
                  <p className="text-gray-700">
                    Use para vendas <strong>no balcão</strong> (comércio eletrônico, PDV) ao consumidor final não identificado. Emissão mais rápida que NFe.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">✅ Dados obrigatórios</h4>
                  <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                    <li>NCM (8 dígitos)</li>
                    <li>Descrição do produto</li>
                    <li>Quantidade e valor</li>
                    <li>CSC e ID Token (da SEFAZ da sua UF)</li>
                    <li>Cliente: pode ser "Consumidor não identificado"</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">⚡ Como preencher</h4>
                  <p className="text-gray-700 text-sm">
                    No <strong>PDV</strong>, marque <strong>"Emitir NFC-e automaticamente"</strong> ou vá em <strong>Pedidos → Emitir Nota Fiscal</strong> e selecione <strong>"NFCe"</strong>.
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm text-blue-800">
                  <p><strong>💡 Dica:</strong> O CSC e ID Token são obtidos no portal SIARE da sua UF (ex: siare.sefaz.sp.gov.br).</p>
                </div>
              </CardContent>
            </Card>

            {/* NFS-e */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
                <CardTitle className="text-amber-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  NFS-e - Nota Fiscal de Serviço Eletrônica
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📌 Quando usar?</h4>
                  <p className="text-gray-700">
                    Use para emissão de <strong>serviços</strong> (consertos, personalizações, consultoria). Cada município tem suas próprias regras.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">✅ Dados obrigatórios</h4>
                  <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                    <li>Código IBGE do município do prestador</li>
                    <li>Código IBGE do município do tomador</li>
                    <li>Item da lista de serviço (ex: 14.01)</li>
                    <li>Alíquota do ISS (%)</li>
                    <li>Inscrição Municipal do prestador</li>
                    <li>Discriminação/descrição do serviço</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">⚡ Como preencher</h4>
                  <p className="text-gray-700 text-sm">
                    1. Acesse <strong>Serviços</strong> e cadastre seus serviços com os dados fiscais (item lista, código tributário, alíquota).<br/>
                    2. Vá em <strong>NF Avulsa</strong> ou <strong>Pedidos → Emitir Nota</strong>, selecione <strong>"NFS-e"</strong> e escolha o serviço para preencher automaticamente.
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-3 rounded text-sm text-amber-800">
                  <p><strong>💡 Dica:</strong> Cada município tem um provedor diferente (RPS, NFSE, Nota Carioca, etc). A equipe do Ateliê Pro pode informar qual usar na sua região.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba: Configuração */}
          <TabsContent value="config" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-indigo-600" />
                  Guia de Configuração Passo a Passo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Seção: Dados da Empresa */}
                <div className="border-l-4 border-blue-400 pl-4 py-2">
                  <h4 className="font-bold text-gray-900 mb-3">1️⃣ Dados da Empresa</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>CNPJ:</strong> Encontre no seu contrato social ou Cartório. Máximo 14 dígitos.</p>
                    <p><strong>Razão Social:</strong> Nome oficial registrado na Junta Comercial.</p>
                    <p><strong>Nome Fantasia (opcional):</strong> Sobrenome comercial da sua loja.</p>
                    <p><strong>Inscrição Estadual (IE):</strong> Código da Sefaz da sua UF. Obtém-se solicitando ao órgão estadual.</p>
                    <p><strong>Inscrição Municipal:</strong> Necessária para NFS-e. Solicite à prefeitura local.</p>
                  </div>
                </div>

                {/* Seção: Endereço */}
                <div className="border-l-4 border-green-400 pl-4 py-2">
                  <h4 className="font-bold text-gray-900 mb-3">2️⃣ Endereço da Empresa</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Logradouro:</strong> Rua, avenida, etc (sem número).</p>
                    <p><strong>Número:</strong> Número do imóvel.</p>
                    <p><strong>Complemento (opcional):</strong> Apto, sala, bloco, etc.</p>
                    <p><strong>Bairro:</strong> Bairro/distrito.</p>
                    <p><strong>Cidade:</strong> Município.</p>
                    <p><strong>UF:</strong> Estado (2 letras: SP, MG, RJ, etc).</p>
                    <p><strong>CEP:</strong> 8 dígitos (apenas números).</p>
                  </div>
                </div>

                {/* Seção: Certificado */}
                <div className="border-l-4 border-yellow-400 pl-4 py-2">
                  <h4 className="font-bold text-gray-900 mb-3">3️⃣ Certificado Digital A1</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>O que é:</strong> Arquivo de segurança (.pfx ou .p12) que identifica sua empresa junto à Sefaz.</p>
                    <p><strong>Como obter:</strong> Solicite a uma Autoridade Certificadora autorizada (Serpro, Serasa, Certisign, etc).</p>
                    <p><strong>Onde encontrar no seu computador:</strong> Geralmente em "Downloads" ou pasta indicada pela AC.</p>
                    <p><strong>Senha:</strong> A senha foi fornecida quando você obteve o certificado. Guarde com segurança.</p>
                    <p className="text-yellow-700 font-semibold">⚠️ Máximo 5MB e em formato .pfx ou .p12.</p>
                  </div>
                </div>

                {/* Seção: Ambiente */}
                <div className="border-l-4 border-purple-400 pl-4 py-2">
                  <h4 className="font-bold text-gray-900 mb-3">4️⃣ Ambiente (Homologação vs Produção)</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Homologação:</strong> Ambiente de testes. Use aqui por 30 dias para testar suas emissões.</p>
                    <p><strong>Produção:</strong> Ambiente real. Use após validar que tudo funciona em homologação.</p>
                    <p className="text-purple-700 font-semibold">💡 Sempre comece em homologação e obtenha o token antes de mudar para produção.</p>
                  </div>
                </div>

                {/* Seção: Tokens */}
                <div className="border-l-4 border-red-400 pl-4 py-2">
                  <h4 className="font-bold text-gray-900 mb-3">5️⃣ Tokens de Autenticação</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Token de Homologação:</strong> Solicitado ao cadastrar o emitente na Focus. Copie exatamente como informado.</p>
                    <p><strong>Token de Produção:</strong> Fornecido pela equipe do Ateliê Pro após aprovação de homologação.</p>
                    <p className="text-red-700 font-semibold">🔐 Nunca compartilhe os tokens. São senhas de acesso à sua conta fiscal.</p>
                  </div>
                </div>

                {/* Seção: NFC-e */}
                <div className="border-l-4 border-blue-400 pl-4 py-2">
                  <h4 className="font-bold text-gray-900 mb-3">6️⃣ Configuração NFC-e (Modelo 65)</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>CSC:</strong> Código de Segurança fornecido pela SEFAZ (geralmente 8 caracteres). Obtém-se no portal SIARE.</p>
                    <p><strong>ID Token:</strong> Identificador único fornecido pela SEFAZ. Encontra-se no mesmo lugar do CSC.</p>
                    <p><strong>Série e Próximo Número (opcionais):</strong> Se sua UF exigir numeração diferente de NF-e.</p>
                    <p className="text-blue-700 font-semibold">📍 Acesse: siare.sefaz.[uf].gov.br (ex: siare.sefaz.sp.gov.br)</p>
                  </div>
                </div>

                {/* Seção: NFS-e */}
                <div className="border-l-4 border-amber-400 pl-4 py-2">
                  <h4 className="font-bold text-gray-900 mb-3">7️⃣ Configuração NFS-e</h4>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Habilitar NFS-e:</strong> Marque se sua prefeitura utiliza sistema de NFS-e.</p>
                    <p><strong>Série/RPS:</strong> Numeração de RPS (Recibo Provisório de Serviço). Consulte sua prefeitura.</p>
                    <p><strong>Login/Senha Prefeitura:</strong> Se sua prefeitura exigir. Geralmente é acesso ao portal municipal.</p>
                    <p className="text-amber-700 font-semibold">📞 Contacte a Secretaria de Finanças da sua prefeitura para obter esses dados.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba: Por UF */}
          <TabsContent value="ufs" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-indigo-600" />
                  Especificidades por Estado (UF)
                </CardTitle>
                <CardDescription>
                  Guia rápido de como configurar em alguns estados
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* SP */}
                <Card className="border-2 border-green-200 bg-green-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge className="bg-green-600">SP</Badge>
                      São Paulo
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-gray-700">
                    <p><strong>SIARE Portal:</strong> https://siare.sefaz.sp.gov.br</p>
                    <p><strong>CSC/ID Token:</strong> Obtém-se no menu "Documentos Eletrônicos → Manutenção Cadastro Emissor"</p>
                    <p><strong>NFS-e:</strong> São Paulo usa "Nota Carioca" (integrada). Solicite inscrição municipal na Prefeitura.</p>
                    <p><strong>Contato SEFAZ-SP:</strong> (11) 3119-5600 ou www.sefaz.sp.gov.br</p>
                  </CardContent>
                </Card>

                {/* MG */}
                <Card className="border-2 border-yellow-200 bg-yellow-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge className="bg-yellow-600">MG</Badge>
                      Minas Gerais
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-gray-700">
                    <p><strong>SIARE Portal:</strong> https://siare.sefaz.mg.gov.br</p>
                    <p><strong>CSC/ID Token:</strong> Em "Minhas Documentações → Informações de Segurança"</p>
                    <p><strong>NFS-e:</strong> Cada prefeitura usa um sistema diferente (RPS, NFSE, Giss, etc). Contate sua prefeitura.</p>
                    <p><strong>Contato SEFAZ-MG:</strong> (31) 3215-8000 ou www.sefaz.mg.gov.br</p>
                  </CardContent>
                </Card>

                {/* RJ */}
                <Card className="border-2 border-blue-200 bg-blue-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge className="bg-blue-600">RJ</Badge>
                      Rio de Janeiro
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-gray-700">
                    <p><strong>SIARE Portal:</strong> https://siare.sefaz.rj.gov.br</p>
                    <p><strong>CSC/ID Token:</strong> Em "Emitente → Informações de Segurança"</p>
                    <p><strong>NFS-e:</strong> Usa "Nota Carioca" para RJ. Rio de Janeiro tem sistema unificado.</p>
                    <p><strong>Contato SEFAZ-RJ:</strong> (21) 2334-9200 ou www.sefaz.rj.gov.br</p>
                  </CardContent>
                </Card>

                {/* BA */}
                <Card className="border-2 border-red-200 bg-red-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge className="bg-red-600">BA</Badge>
                      Bahia
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-gray-700">
                    <p><strong>SIARE Portal:</strong> https://siare.sefaz.ba.gov.br</p>
                    <p><strong>CSC/ID Token:</strong> Em "Gerenciamento → Dados de Segurança"</p>
                    <p><strong>NFS-e:</strong> Salvador e algumas cidades usam NFS-e. Verifique com a prefeitura.</p>
                    <p><strong>Contato SEFAZ-BA:</strong> (71) 3115-8000 ou www.sefaz.ba.gov.br</p>
                  </CardContent>
                </Card>

                {/* Outras UFs */}
                <Card className="border-2 border-gray-200 bg-gray-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Outros Estados</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-gray-700">
                    <p><strong>Padrão SIARE:</strong> https://siare.sefaz.[uf].gov.br (substitua [uf] pela sigla do seu estado)</p>
                    <p><strong>CSC e ID Token:</strong> Geralmente encontrados em seção de "Segurança" ou "Documentos Eletrônicos"</p>
                    <p><strong>NFS-e:</strong> Varia por município. Entre em contato com sua prefeitura local para informações específicas.</p>
                    <p className="font-semibold text-blue-700">💡 Caso tenha dúvida, consulte a SEFAZ do seu estado ou o painel Focus NF para mais detalhes.</p>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* FAQ */}
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <HelpCircle className="w-5 h-5" />
              Dúvidas Frequentes (FAQ)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 mb-1">❓ Qual a diferença entre NFe e NFCe?</p>
              <p className="text-sm text-gray-700">NFe é para vendas entre empresas ou consumidor identificado. NFCe é para vendas no balcão (consumidor final) — mais rápida e simplificada.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">❓ Posso usar o mesmo certificado para NFe e NFCe?</p>
              <p className="text-sm text-gray-700">Sim! O certificado é único por CNPJ. NFe e NFCe usam o mesmo certificado, mas CSC/ID Token são específicos de NFCe.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">❓ O que fazer se a emissão falhar com erro "539"?</p>
              <p className="text-sm text-gray-700">Erro 539 é duplicidade de número. Significa que o "Próximo Número" está repetido. Ajuste o valor em "Configuração Focus NF" conforme indicado na contabilidade.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">❓ Preciso de inscrição municipal para emitir NFe?</p>
              <p className="text-sm text-gray-700">Não é obrigatório para NFe. Mas é necessário para NFS-e. Cada município tem suas regras — consulte a prefeitura.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">❓ Como obtenho o token de homologação?</p>
              <p className="text-sm text-gray-700">Após cadastrar o emitente na Focus, você receberá um token de homologação automaticamente. Copie exatamente como informado e preencha no campo "Token Homologação".</p>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white border-0">
          <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-lg mb-1">Pronto para começar?</h3>
              <p className="text-indigo-100">Acesse a Configuração Focus NF e preencha os dados da sua empresa.</p>
            </div>
            <Button
              onClick={() => navigate("/configuracao-focusnf")}
              className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold whitespace-nowrap"
            >
              Ir para Configuração
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
